"use client";

import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Icon } from "./icon";

import { AgentContactCard } from "@/components/agent-contact-card";
import { InquiryForm } from "@/components/profile/inquiry-form";
import type { ProfileAgent } from "@/components/profile/profile-types";
import { firstNameOf } from "@/lib/format";

export interface ContactRailProps {
  agent: ProfileAgent;
  vcardContent: string;
}

/** Profile contact rail: shared contact card, then the existing QR and inquiry. */
export function ContactRail({ agent, vcardContent }: ContactRailProps) {
  const vcardHref = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardContent)}`;
  const firstName = firstNameOf(agent.name);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  return (
    <aside className="ad-profile-contact min-w-0 self-start lg:col-start-2 lg:row-start-1 lg:row-span-2">
      <AgentContactCard
        agentId={agent.agentId}
        name={agent.name}
        photoUrl={agent.photoUrl}
        phone={agent.phone}
        brokerageName={agent.brokerage}
        brokerageAddress={agent.brokerageAddress}
        brokeragePhone={agent.brokeragePhone}
        licenseNumber={agent.licenseNumber}
        licenseState={agent.licenseState}
        showProfileLink={false}
        surface="contact_rail"
        heading="Agent Contact"
        actions={<button ref={opener} type="button" onClick={() => dialog.current?.showModal()}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 font-semibold"
          style={{ backgroundColor: "var(--brand-button-bg, var(--brand-primary))", color: "var(--brand-primary-foreground)" }}>
          <Icon name="mail" size={18} />Email {firstName}
        </button>}
      >

      <div className="flex items-center gap-4 border-t pt-4" style={{ borderColor: "var(--brand-card-border)" }}>
        <div className="shrink-0">
          <QRCodeSVG
            value={vcardContent}
            size={100}
            level="M"
            title="Scan to save this agent to your contacts"
          />
        </div>
        <div className="min-w-0">
        <h3
          className="text-[11.5px] font-bold uppercase tracking-[0.08em]"
          style={{ color: "var(--brand-primary-text, var(--brand-primary))" }}
        >
          Save to your contacts
        </h3>
        <a
          href={vcardHref}
          download={`${agent.slug}.vcf`}
          className="mt-3 inline-flex items-center justify-center gap-1.5 text-[13.5px] font-medium"
          style={{ color: "var(--brand-primary-text, var(--brand-primary))" }}
        >
          <Icon name="download" size={16} />
          Download contact card
        </a>
        </div>
      </div>
      </AgentContactCard>

      <dialog ref={dialog} aria-labelledby="profile-inquiry-heading" aria-describedby="profile-inquiry-description"
        onClose={() => opener.current?.focus()}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]");
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
          }
        }}
        className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-lg border p-5 backdrop:bg-black/40"
        style={{ backgroundColor: "var(--brand-card-bg)", borderColor: "var(--brand-card-border)", color: "var(--brand-text)" }}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id="profile-inquiry-heading" className="text-xl font-semibold">Email {firstName}</h2>
          <button type="button" aria-label="Close contact form" onClick={() => dialog.current?.close()}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md">
            <Icon name="close" size={20} />
          </button>
        </div>
        <p id="profile-inquiry-description" className="mb-4 text-sm" style={{ color: "var(--brand-text-muted)" }}>
          Tell {firstName} a little about your real estate needs.
        </p>
        <InquiryForm slug={agent.slug} heading={`Email ${firstName}`} embedded />
      </dialog>
    </aside>
  );
}
